import { attr, hasMany, type AsyncHasMany } from '@ember-data/model';
import { service } from '@ember/service';
import type { Type } from '@warp-drive/core-types/symbols';
import type StoreService from 'scaleflow/services/store';
import nameSort from 'scaleflow/utils/nameSort';
import type CommentModel from './comment';
import type ConversationModel from './conversation';
import type IdeaModel from './idea';
import type InitiativeModel from './initiative';
import type LlModel from './ll';
import OwnerModel from './owner';
import type QuestionModel from './question';
import type RoleModel from './role';
import type TeamModel from './team';
import type TfoModel from './tfo';
import type UncertaintyModel from './uncertainty';
import type WfaModel from './wfa';
import type WorkStreamModel from './workstream';

export default class PersonModel extends OwnerModel {
  declare [Type]: 'person';
  @service declare store: StoreService;

  @attr('string') declare name: string;
  @attr('string') declare abbreviation: string;
  // @attr('string') color;
  @attr('boolean') declare is_claimed: boolean;
  @attr declare favorites: unknown;
  @attr declare active: boolean;
  @hasMany('question', {
    // polymorphic: true,
    async: true,
    inverse: 'author',
  })
  declare questions: AsyncHasMany<QuestionModel>;
  @hasMany('tfo', {
    // polymorphic: true,
    async: true,
    inverse: 'author',
  })
  declare tfos: AsyncHasMany<TfoModel>;
  @hasMany('ll', {
    // polymorphic: true,
    async: true,
    inverse: 'author',
  })
  declare lls: AsyncHasMany<LlModel>;
  @hasMany('uncertainty', {
    // polymorphic: true,
    async: true,
    inverse: 'author',
  })
  declare uncertainties: AsyncHasMany<UncertaintyModel>;
  @hasMany('wfa', {
    // polymorphic: true,
    async: true,
    inverse: 'author',
  })
  declare wfas: AsyncHasMany<WfaModel>;

  @hasMany('comment', {
    async: true,
    inverse: 'author',
  })
  declare comments: AsyncHasMany<CommentModel>;
  @hasMany('workstream', {
    // polymorphic: true,
    async: true,
    inverse: 'author',
  })
  declare workstreams: AsyncHasMany<WorkStreamModel>;

  @hasMany('initiative', {
    // polymorphic: true,
    async: true,
    inverse: 'productManager',
  })
  declare initiatives: AsyncHasMany<InitiativeModel>;

  @hasMany('role', { async: true, inverse: 'people' })
  declare roles: AsyncHasMany<RoleModel>;

  @hasMany('team', { async: true, inverse: 'people' })
  declare teams: AsyncHasMany<TeamModel>;

  @hasMany('idea', { async: true, inverse: 'submitter' })
  declare ideas: AsyncHasMany<IdeaModel>;

  @hasMany('conversation', {
    async: true,
    inverse: 'person',
  })
  declare conversations: AsyncHasMany<ConversationModel>;

  get favoriteInitiatives() {
    const _initiatives = this.store
      .peekAll<InitiativeModel>('initiative')
      .filter((item) => item['favorite'] === true)
      .sort((a, b) => nameSort(a.title, b.title));
    return _initiatives;
  }

  get favoriteTeams() {
    const teams = this.store
      .peekAll<TeamModel>('team')
      .filter((item) => item['favorite'] === true)
      .sort((a, b) => nameSort(a.title, b.title));
    return teams;
  }

  get firstRole() {
    return [...(this.roles.content ?? [])].sort((a, b) =>
      nameSort(a.name, b.name),
    )[0];
  }

  get activeTeams() {
    return (this.teams.content ?? []).filter((team) => team.active);
  }
}
